<script setup lang="ts">
import type { SidebarProps } from "@/components/ui/sidebar";

import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { CalendarDays, Users, Shield, User } from "@lucide/vue";
import NavMain from "@/components/NavMain.vue";
import NavUser from "@/components/NavUser.vue";
import TeamSwitcher from "@/components/TeamSwitcher.vue";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/auth";
import { useTeamStore } from "@/stores/team";
import { useTeamsServiceListTeams } from "@/api/teams/teams";
import { useMyRoles } from "@/features/planning/composables/useMyRoles";
import { Role, hasRole } from "@/features/team/lib/roles";

const props = withDefaults(defineProps<SidebarProps>(), {
  collapsible: "icon",
});

const { t } = useI18n();
const route = useRoute();
const { isMobile, setOpenMobile } = useSidebar();
const auth = useAuthStore();
const teamStore = useTeamStore();
const { data: teamsData } = useTeamsServiceListTeams();

const user = computed(() => ({
  name: auth.displayName || auth.user?.email || "",
  email: auth.user?.email ?? "",
  avatar: "",
}));

const teams = computed(() => teamsData.value?.teams ?? []);

const teamId = computed(() => teamStore.currentTeamId ?? "");
const myRoles = useMyRoles(teamId);
const isManager = computed(() => hasRole(myRoles.value, Role.Manager));

// On mobile the sidebar is a sheet over the page: close it whenever the route
// changes, whichever control triggered it (nav link, team switch, account menu,
// logout, guard redirect).
watch(
  () => route.fullPath,
  () => {
    if (isMobile.value) setOpenMobile(false);
  },
);

// A user with no team (ListTeams loaded and empty) has nothing to plan against.
const hasNoTeam = computed(() => !!teamsData.value && teams.value.length === 0);

// Role-aware nav: coaches and athletes get Planning (teamless users get only Account);
// only managers see Team; only platform admins see Admin.
const canPlan = computed(
  () => hasRole(myRoles.value, Role.Coach) || hasRole(myRoles.value, Role.Athlete),
);
const navMain = computed(() => [
  ...(hasNoTeam.value
    ? [{ title: t("nav.account"), url: "/account", icon: User }]
    : canPlan.value
      ? [{ title: t("nav.planning"), url: "/planning", icon: CalendarDays }]
      : []),
  ...(isManager.value ? [{ title: t("nav.team"), url: "/team", icon: Users }] : []),
  ...(auth.isPlatformAdmin ? [{ title: t("nav.admin"), url: "/admin", icon: Shield }] : []),
]);
</script>

<template>
  <Sidebar v-bind="props">
    <SidebarHeader>
      <TeamSwitcher :teams="teams" />
    </SidebarHeader>
    <SidebarContent>
      <NavMain :items="navMain" />
    </SidebarContent>
    <SidebarFooter>
      <NavUser :user="user" />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>
